import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Geolocation, Position, PermissionStatus as CapacitorPermissionStatus } from '@capacitor/geolocation';
import { from, Observable, of, throwError } from 'rxjs';
import { catchError, map, shareReplay, switchMap, timeout } from 'rxjs/operators';
import { MapService } from '../../shared/map/map.service';
import {HttpClient} from '@angular/common/http';
import {environment} from '../../../environments/environment';

export interface Coordinates {
    lat: number;
    lng: number;
}

export type LocationSource = 'device' | 'ip' | 'profile' | 'unknown';
export interface ResolvedLocation { coordinates: Coordinates | null; source: LocationSource; }

@Injectable({
    providedIn: 'root'
})
export class UserLocationService {
    private readonly isBrowser: boolean;

    constructor(
        private mapService: MapService,
        private http: HttpClient,
        @Inject(PLATFORM_ID) platformId: object
    ) {
        this.isBrowser = isPlatformBrowser(platformId);
    }

    private resolvedLocation$?: Observable<ResolvedLocation>;
    private devicePosition$?: Observable<Coordinates>;

    public resolveMapLocation(fallbackCity?: string): Observable<ResolvedLocation> {
        if (!this.isBrowser) {
            return of({coordinates: null, source: 'unknown'});
        }
        if (this.resolvedLocation$) {
            return this.resolvedLocation$;
        }
        this.resolvedLocation$ = this.getPermittedDevicePosition().pipe(
            timeout({first: 5000}),
            map((coordinates) => ({coordinates, source: 'device' as const})),
            catchError(() => this.http.get<{coordinates?: number[] | null} | null>(`${environment.apiUrl}/geocode/approximate-location`).pipe(
                map((result) => result?.coordinates?.length === 2
                    ? {coordinates: {lat: Number(result.coordinates[0]), lng: Number(result.coordinates[1])}, source: 'ip' as const}
                    : null),
                catchError(() => of(null)),
                switchMap((ipLocation) => {
                    if (ipLocation) {
                        return of(ipLocation);
                    }
                    if (!fallbackCity) {
                        return of({coordinates: null, source: 'unknown' as const});
                    }
                    return this.mapService.getCoordinates(fallbackCity, 'pl').pipe(
                        map((result: any) => ({coordinates: {lat: Number(result.lat), lng: Number(result.lon)}, source: 'profile' as const})),
                        catchError(() => of({coordinates: null, source: 'unknown' as const}))
                    );
                })
            )),
            shareReplay(1)
        );
        return this.resolvedLocation$;
    }

    /**
     * Get user position.
     * Tries to use Device Geolocation first.
     * If failed/denied and fallbackCity is provided, geocodes the city.
     */
    public getPosition(fallbackCity?: string): Observable<Coordinates> {
        if (!this.isBrowser) {
            return throwError(() => new Error('Geolocation unavailable during SSR'));
        }

        return this.getDevicePosition().pipe(
            catchError((error) => {
                if (fallbackCity) {
                    return this.mapService.getCoordinates(fallbackCity, 'pl').pipe(
                        map((result: any) => {
                            if (result && result.lat && result.lon) {
                                return {lat: parseFloat(result.lat), lng: parseFloat(result.lon)};
                            }
                            throw new Error('Geocoding failed');
                        })
                    );
                }
                return throwError(() => error);
            })
        );
    }

    private getDevicePosition(): Observable<Coordinates> {
        if (this.devicePosition$) {
            return this.devicePosition$;
        }

        this.devicePosition$ = from(Geolocation.getCurrentPosition({
            enableHighAccuracy: true,
            timeout: 5000,
            maximumAge: 60000
        })).pipe(
            map((position: Position) => ({
                lat: position.coords.latitude,
                lng: position.coords.longitude
            })),
            catchError((error) => {
                console.warn('UserLocationService: Geolocation failed or denied', error);
                return throwError(() => error);
            }),
            shareReplay(1)
        );
        return this.devicePosition$;
    }

    private getPermittedDevicePosition(): Observable<Coordinates> {
        return from(this.hasGrantedLocationPermission()).pipe(
            switchMap((granted) => {
                if (granted) {
                    return this.getDevicePosition();
                }
                return throwError(() => new Error('Geolocation permission has not been granted'));
            })
        );
    }

    private async hasGrantedLocationPermission(): Promise<boolean> {
        try {
            const capacitorStatus = await Geolocation.checkPermissions();
            if (capacitorStatus.location === 'granted' || capacitorStatus.coarseLocation === 'granted') {
                return true;
            }
        } catch {
            // Use the browser permission API below when the Capacitor bridge is unavailable.
        }

        // On desktop Chrome Capacitor can report "prompt" even after the browser
        // has granted geolocation. Query the browser permission without prompting.
        try {
            const permission = await navigator.permissions.query({name: 'geolocation'} as PermissionDescriptor);
            return permission.state === 'granted';
        } catch {
            return false;
        }
    }

    public async requestPermissions(): Promise<CapacitorPermissionStatus> {
        if (!this.isBrowser) {
            return { location: 'denied', coarseLocation: 'denied' } as CapacitorPermissionStatus;
        }

        const status = await Geolocation.checkPermissions();
        if (status.location !== 'granted') {
            return await Geolocation.requestPermissions();
        }
        return status;
    }
}
